import { CheckOutlined, CloseOutlined } from '@ant-design/icons';
import { Space, Switch, Spin, message, Button, Input, Divider, notification } from 'antd';
import { NamePath } from 'antd/es/form/interface';
import React, { useCallback, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { Trans, useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';

import useAPIMutation from '../../../api/useAPIMutation';
import useAPIQuery from '../../../api/useAPIQuery';
import useInfiniteAPIQuery from '../../../api/useInfiniteAPIQuery';
import ErrorResult from '../../../components/ErrorResult';
import Form from '../../../components/Form';
import usePrefetchPages from '../../../hooks/usePrefetchPages';
import { t } from '../../../i18n';
import { withRouteChange } from '../../../services/APM';
import { VixtingCompany } from '../../../types';

interface StaffVixtingConfigurationFormValues {
  enableVixtingAdmission: boolean;
  enableVixtingUnitBusiness: boolean;
  vixtingMainCompanyToken: string;
  companies: VixtingCompany[];
}

const validateUniqueToken = (
  currentCompanyId: string,
  companies: VixtingCompany[],
  getFieldsValue: (nameList: NamePath[]) => StaffVixtingConfigurationFormValues
) => ({
  validator(_: unknown, value: string | undefined) {
    const fields_values =
      getFieldsValue(
        companies.filter((c) => c.id !== currentCompanyId).map((c) => ['companies', c.id, 'token'])
      )?.companies ?? {};

    const tokens = Object.values(fields_values).map((c) => c.token) as string[];

    if (value && tokens.includes(value)) {
      return Promise.reject(new Error(t('O token deve ser único para cada empresa!') as string));
    }
    return Promise.resolve();
  },
});

const StaffVixtingConfiguration = () => {
  const { t } = useTranslation();

  const queryClient = useQueryClient();
  const [form] = Form.useForm<StaffVixtingConfigurationFormValues>();
  const enableVixtingAdmissionValue = Form.useWatch('enableVixtingAdmission', form);

  const config = useAPIQuery('/configs/tenant-configurations/');

  const mutation = useAPIMutation(
    async (values: StaffVixtingConfigurationFormValues, api) => {
      return await Promise.all([
        api.patch('/configs/tenant-configurations/enable-vixting-admission/', {
          value: values.enableVixtingAdmission,
        }),
        values.enableVixtingAdmission &&
          api.patch('/candidate-register-fields/cpf/', {
            visible: 'YES',
            required: 'YES',
          }),
        values.enableVixtingAdmission &&
          api.patch('/configs/tenant-configurations/enable-vixting-unit-business/', {
            value: values.enableVixtingUnitBusiness,
          }),
        api.patch('/configs/tenant-configurations/vixting-company-token/', {
          value: values.vixtingMainCompanyToken,
        }),
        values.companies.length > 0 &&
          api.patch('/vixting/vixting-companies/create-or-update-companies/', values.companies),
      ]);
    },
    {
      onSuccess: () => {
        message.success(t('Configurações atualizadas.'));
      },
      onError: async (err, variables, context) => {
        if (err?.response?.data?.error === 'sankhya_admission_already_enabled') {
          return notification.error({
            message: t('Erro'),
            description: t(
              'A Integração com Admissão Digital da Vixting não pode ser habilitada enquanto a Integração com Admissão Digital da Sankhya está ativa.'
            ),
          });
        }
        return queryClient.defaultMutationOptions().onError?.(err, variables, context);
      },
      onSettled: async () => {
        await queryClient.invalidateQueries('/configs/tenant-configurations/');
        await queryClient.invalidateQueries({
          predicate: (query) =>
            !!query.queryKey &&
            Array.isArray(query.queryKey) &&
            typeof query.queryKey?.[0] == 'string' &&
            (query.queryKey[0] as string).includes('vixting'),
        });
      },
    }
  );
  const vixtingCompaniesPages = useInfiniteAPIQuery(['/vixting/companies/'], {
    enabled: !!config.data?.enableVixtingAdmission,
    staleTime: 5000 * 60,
    onError: () => {
      message.error(t('Falha ao carregar empresas da vixting!'));
    },
    pageKey: 'offsetPage',
  });
  const vixtingCompanies = usePrefetchPages(vixtingCompaniesPages, (data) => data.results);

  const vixtingLocalCompaniesPages = useInfiniteAPIQuery(['/vixting/vixting-companies/'], {
    enabled: !!config.data?.enableVixtingAdmission,
    staleTime: 5000 * 60,
    onError: () => {
      message.error(t('Falha ao carregar empresas!'));
    },
  });
  const vixtingLocalCompanies = usePrefetchPages(
    vixtingLocalCompaniesPages,
    (data) => data.results
  );
  const vixtingRemoteCompaniesMap: Record<string, VixtingCompany> = useMemo(
    () => vixtingCompanies.reduce((a, c) => ({ ...a, [c.id]: c }), {}),
    [vixtingCompanies]
  );
  const vixtingLocalCompaniesMap: Record<string, VixtingCompany> = useMemo(
    () => vixtingLocalCompanies.reduce((a, c) => ({ ...a, [c.id]: c }), {}),
    [vixtingLocalCompanies]
  );
  const mergedCompanies = useMemo(() => {
    return !vixtingCompaniesPages.isLoading && !vixtingLocalCompaniesPages.isLoading
      ? vixtingCompanies?.map(
          (c) =>
            ({
              id: c.id,
              name: c.fantasyName,
              token: vixtingLocalCompaniesMap?.[c.id]?.token || '',
              is_active: vixtingLocalCompaniesMap?.[c.id]?.is_active,
            } as VixtingCompany)
        )
      : [];
  }, [
    vixtingCompaniesPages.isLoading,
    vixtingLocalCompaniesPages.isLoading,
    vixtingCompanies,
    vixtingLocalCompaniesMap,
  ]);

  const mergedCompaniesMap = useMemo(() => {
    return new Map(mergedCompanies.map((company) => [company.id, company]));
  }, [mergedCompanies]);

  const hasCompanyChanged = useCallback(
    (company: VixtingCompany, mergedCompany: VixtingCompany) => {
      return (
        (company.token !== undefined && mergedCompany.token !== company.token) ||
        (company.is_active !== undefined && mergedCompany.is_active !== company.is_active)
      );
    },
    []
  );

  return (
    <ErrorResult error={config.error}>
      <Helmet>
        <title>
          {t('Integração Vixting | Área do CS')} | {t('Recrutamento e Seleção')}
        </title>
      </Helmet>
      <Spin
        spinning={
          config.isLoading ||
          vixtingCompaniesPages.isLoading ||
          vixtingLocalCompaniesPages.isLoading
        }
      >
        <Form<{
          enableVixtingAdmission: boolean;
          vixtingMainCompanyToken: string;
          enableVixtingUnitBusiness: boolean;
          companies: VixtingCompany[];
        }>
          form={form}
          layout="vertical"
          initialValues={{
            enableVixtingAdmission: config.data?.enableVixtingAdmission,
            enableVixtingUnitBusiness: config.data?.enableVixtingUnitBusiness,
            vixtingMainCompanyToken: config.data?.vixtingMainCompanyToken,
          }}
          onFinish={(variables) => {
            const modifiedFields: StaffVixtingConfigurationFormValues = form.getFieldsValue(
              true,
              (meta: { touched: boolean; validating: boolean }) => meta.touched
            );
            const companies = (
              modifiedFields.companies ? Object.entries(modifiedFields.companies) : []
            )
              .map(
                ([key, company]: [string, VixtingCompany]) =>
                  ({
                    id: key,
                    name: vixtingRemoteCompaniesMap[key].fantasyName,
                    token: company.token,
                    is_active: company.is_active,
                  } as VixtingCompany)
              )
              .filter((company) => {
                const mergedCompany = mergedCompaniesMap.get(company.id);
                return !mergedCompany || hasCompanyChanged(company, mergedCompany);
              });

            mutation.mutate({
              enableVixtingAdmission: variables?.enableVixtingAdmission,
              vixtingMainCompanyToken: variables?.vixtingMainCompanyToken,
              enableVixtingUnitBusiness: variables?.enableVixtingUnitBusiness,
              companies,
            });
          }}
        >
          <Space direction="vertical">
            <Form.Row align="middle">
              <Form.Col>
                <Form.Item
                  name="enableVixtingAdmission"
                  valuePropName="checked"
                  style={{ marginBottom: 0 }}
                >
                  <Switch />
                </Form.Item>
              </Form.Col>
              <Form.Col>
                <Trans>Habilitar Integração com Admissão Digital da Vixting</Trans>
              </Form.Col>
            </Form.Row>
            <Form.Row align="middle">
              <Form.Col>
                <Form.Item
                  name="enableVixtingUnitBusiness"
                  valuePropName="checked"
                  style={{ marginBottom: 0 }}
                >
                  <Switch disabled={!enableVixtingAdmissionValue} />
                </Form.Item>
              </Form.Col>
              <Form.Col>
                <Trans>Habilitar Unidades de negócio na integração Vixting</Trans>
              </Form.Col>
            </Form.Row>
            <Form.Row align="middle">
              <Form.Col span={8}>
                <Form.Item
                  name="vixtingMainCompanyToken"
                  label={t('Token de Integração da Empresa Matriz')}
                  rules={[{ required: enableVixtingAdmissionValue }]}
                >
                  <Input disabled={!enableVixtingAdmissionValue} />
                </Form.Item>
              </Form.Col>
            </Form.Row>
          </Space>

          {mergedCompanies.map((company) => (
            <React.Fragment key={company.id}>
              <Form.Row align="middle">
                <Form.Col span={14}>{company.name || company.fantasyName}</Form.Col>
                <Form.Col span={2}>
                  <Form.Item
                    name={['companies', company.id, 'is_active']}
                    rules={[{ required: false }]}
                    valuePropName="checked"
                    initialValue={company?.is_active || false}
                  >
                    <Switch
                      checkedChildren={<CheckOutlined />}
                      unCheckedChildren={<CloseOutlined />}
                      disabled={!enableVixtingAdmissionValue}
                    />
                  </Form.Item>
                </Form.Col>
                <Form.Col span={8}>
                  <Form.Item
                    name={['companies', company.id, 'token']}
                    rules={[
                      (form) => ({
                        required: form.getFieldValue(['companies', company.id, 'is_active']),
                      }),
                      ({ getFieldsValue }) =>
                        validateUniqueToken(company.id, mergedCompanies, getFieldsValue),
                    ]}
                    initialValue={company.token || ''}
                  >
                    <Input.Password
                      disabled={!enableVixtingAdmissionValue}
                      placeholder={t('Token')}
                    />
                  </Form.Item>
                </Form.Col>
              </Form.Row>
              <Divider />
            </React.Fragment>
          ))}
          <Form.Footer>
            <Form.FooterEnd>
              <Form.Item noStyle shouldUpdate={() => true}>
                {(form) => (
                  <Button onClick={() => form.resetFields()}>
                    <Trans>Cancelar</Trans>
                  </Button>
                )}
              </Form.Item>
              <Button type="primary" htmlType="submit" loading={mutation.isLoading}>
                <Trans>Salvar</Trans>
              </Button>
            </Form.FooterEnd>
          </Form.Footer>
        </Form>
      </Spin>
    </ErrorResult>
  );
};

export default withRouteChange()(StaffVixtingConfiguration);
